"use client"

import { useState } from "react"
import { Form, Input, Button, Typography, Select, Space, message } from "antd"
import {
  ArrowLeftOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  RightOutlined,
  DownOutlined,
  ForkOutlined,
  CopyOutlined,
  UserAddOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons"

const { Title, Text } = Typography
const { Option } = Select

export default function MixpanelForm({ onBack }) {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [optionalFieldsOpen, setOptionalFieldsOpen] = useState(false)

  const handleSubmit = async (values) => {
    setLoading(true)
    try {
      await new Promise((resolve) => setTimeout(resolve, 1500))
      console.log("Form submitted with values:", values)
      message.success({
        content: "Mixpanel source created successfully!",
        icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
      })
      form.resetFields()
      setOptionalFieldsOpen(false) // Close optional fields on success
    } catch (error) {
      message.error("Failed to create source. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleSubmitFailed = (errorInfo) => {
    message.error("Please fix the errors in the form before submitting.")
    console.log("Form validation failed:", errorInfo)
  }

  const handleCopyJSON = () => {
    const formValues = form.getFieldsValue()
    const jsonConfig = JSON.stringify(formValues, null, 2)
    navigator.clipboard.writeText(jsonConfig)
    message.success("Configuration copied to clipboard!")
  }

  const validateProjectId = (_, value) => {
    if (!value) return Promise.resolve()
    if (!/^[a-zA-Z0-9]+$/.test(value)) {
      return Promise.reject(new Error("Project ID can only contain letters and numbers"))
    }
    if (value.length < 6) {
      return Promise.reject(new Error("Project ID must be at least 6 characters"))
    }
    return Promise.resolve()
  }

  const validateSecret = (_, value) => {
    if (!value) return Promise.resolve()
    if (value.length < 32) {
      return Promise.reject(new Error("Secret must be at least 32 characters long"))
    }
    if (!/^[a-zA-Z0-9]+$/.test(value)) {
      return Promise.reject(new Error("Secret can only contain letters and numbers"))
    }
    return Promise.resolve()
  }

  const validateUsername = (_, value) => {
    if (!value) return Promise.resolve()
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(value)) {
      return Promise.reject(new Error("Please enter a valid email address"))
    }
    return Promise.resolve()
  }

  const validateNumber = (_, value) => {
    if (!value) return Promise.resolve()
    if (!/^\d+$/.test(value)) {
      return Promise.reject(new Error("Must be a number"))
    }
    return Promise.resolve()
  }

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        padding: "48px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {/* Back button */}
      {onBack && (
        <Button
          type="text"
          onClick={onBack}
          style={{
            position: "absolute",
            top: "24px",
            left: "24px",
            color: "#6b7280", // Gray text
            fontSize: "16px",
            display: "flex",
            alignItems: "center",
            padding: "8px 12px",
            borderRadius: "8px",
          }}
        >
          <ArrowLeftOutlined style={{ marginRight: "8px" }} />
          Back
        </Button>
      )}

      <div
        style={{
          width: "100%",
          maxWidth: "800px",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "32px",
            borderBottom: "1px solid #e5e7eb", // Light border
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Title level={2} style={{ margin: 0, color: "#1f2937", fontSize: "28px"}}>
              Create a Mixpanel Source
            </Title>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "#f3f4f6", // Gray-100
                color: "#374151", // Gray-700
                padding: "6px 16px",
                borderRadius: "9999px", // Full rounded
                fontSize: "13px",
                fontWeight: "600",
                border: "1px solid #d1d5db", // Gray-300
                boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
              }}
            >
              <div
                style={{
                  width: "24px",
                  height: "24px",
                  background: "linear-gradient(45deg, #ff6b6b, #4ecdc4, #45b7d1, #96ceb4)", // Mixpanel gradient
                  borderRadius: "9999px", // Full rounded
                  marginRight: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontWeight: "bold",
                  fontSize: "14px",
                }}
              >
                M
              </div>
              Mixpanel
            </div>
          </div>
          <Text style={{ color: "#4b5563", fontSize: "16px" }}>
            Connect your Mixpanel account to seamlessly sync your data.
          </Text>
        </div>

        <div style={{ padding: "32px" }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onFinishFailed={handleSubmitFailed}
            autoComplete="off"
            initialValues={{ sourceName: "Mixpanel", authentication: "Service Account" }}
            style={{ display: "flex", flexDirection: "column", gap: "24px" }}
          >
            {/* Main form fields */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "24px" }}>
              <Form.Item
                name="sourceName"
                label={
                  <Text style={{ color: "#374151", fontSize: "15px", fontWeight: "500" }}>
                    Source name <span style={{ color: "#ef4444" }}>*</span>
                  </Text>
                }
                rules={[{ required: true, message: "Source name is required" }]}
                style={{ marginBottom: 0 }}
              >
                <Input
                  placeholder="e.g., My Mixpanel Data"
                  style={{ height: "44px", fontSize: "15px", borderRadius: "6px" }}
                />
              </Form.Item>

              <Form.Item
                name="authentication"
                label={
                  <Text style={{ color: "#374151", fontSize: "15px", fontWeight: "500" }}>
                    Authentication <span style={{ color: "#ef4444" }}>*</span>
                  </Text>
                }
                rules={[{ required: true, message: "Authentication method is required" }]}
                style={{ marginBottom: 0 }}
              >
                <Select
                  placeholder="Select Authentication Method"
                  style={{ height: "44px", fontSize: "15px", borderRadius: "6px" }}
                  dropdownStyle={{ fontSize: "15px" }}
                >
                  <Option value="Service Account">Service Account</Option>
                  <Option value="Project Secret">Project Secret</Option>
                  <Option value="API Key">API Key</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="username"
                label={
                  <Text style={{ color: "#374151", fontSize: "15px", fontWeight: "500" }}>
                    Username <span style={{ color: "#ef4444" }}>*</span>
                  </Text>
                }
                rules={[{ required: true, message: "Username is required" }, { validator: validateUsername }]}
                style={{ marginBottom: 0 }}
              >
                <Input
                  placeholder="Enter your username/email"
                  style={{ height: "44px", fontSize: "15px", borderRadius: "6px" }}
                  autoComplete="off"
                />
              </Form.Item>

              <Form.Item
                name="secret"
                label={
                  <Text style={{ color: "#374151", fontSize: "15px", fontWeight: "500" }}>
                    Secret <span style={{ color: "#ef4444" }}>*</span>
                  </Text>
                }
                rules={[{ required: true, message: "Secret is required" }, { validator: validateSecret }]}
                style={{ marginBottom: 0 }}
              >
                <Input.Password
                  placeholder="Enter your secret"
                  autoComplete="new-password"
                  iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                  style={{ height: "44px", fontSize: "15px", borderRadius: "6px" }}
                />
              </Form.Item>

              <Form.Item
                name="projectId"
                label={
                  <Text style={{ color: "#374151", fontSize: "15px", fontWeight: "500" }}>
                    Project ID <span style={{ color: "#ef4444" }}>*</span>
                  </Text>
                }
                rules={[{ required: true, message: "Project ID is required" }, { validator: validateProjectId }]}
                style={{ marginBottom: 0 }}
              >
                <Input
                  placeholder="Enter your project ID"
                  style={{ height: "44px", fontSize: "15px", borderRadius: "6px" }}
                />
              </Form.Item>
            </div>

            {/* Optional fields toggle */}
            <div style={{ paddingTop: "16px" }}>
              <Button
                type="text"
                onClick={() => setOptionalFieldsOpen(!optionalFieldsOpen)}
                style={{
                  padding: "8px 0",
                  color: "#374151",
                  fontSize: "16px",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {optionalFieldsOpen ? (
                  <DownOutlined style={{ marginRight: "8px", fontSize: "14px" }} />
                ) : (
                  <RightOutlined style={{ marginRight: "8px", fontSize: "14px" }} />
                )}
                Advanced Configuration (Optional)
              </Button>
            </div>

            {optionalFieldsOpen && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: "24px",
                  padding: "24px",
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb", // Gray-200
                }}
              >
                <Form.Item
                  name="region"
                  label={<Text style={{ color: "#374151", fontSize: "15px", fontWeight: "500" }}>Region</Text>}
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    placeholder="Select Region"
                    style={{ height: "44px", fontSize: "15px", borderRadius: "6px" }}
                    dropdownStyle={{ fontSize: "15px" }}
                    allowClear
                  >
                    <Option value="US">United States</Option>
                    <Option value="EU">Europe</Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  name="dateWindowSize"
                  label={
                    <Text style={{ color: "#374151", fontSize: "15px", fontWeight: "500" }}>Date Window Size</Text>
                  }
                  rules={[{ validator: validateNumber }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input
                    placeholder="Enter date window size (optional)"
                    style={{ height: "44px", fontSize: "15px", borderRadius: "6px" }}
                  />
                </Form.Item>

                <Form.Item
                  name="attributionWindow"
                  label={
                    <Text style={{ color: "#374151", fontSize: "15px", fontWeight: "500" }}>Attribution Window</Text>
                  }
                  rules={[{ validator: validateNumber }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input
                    placeholder="Enter attribution window (optional)"
                    style={{ height: "44px", fontSize: "15px", borderRadius: "6px" }}
                  />
                </Form.Item>
              </div>
            )}

            {/* Action buttons */}
            <Form.Item style={{ marginTop: "32px", marginBottom: 0, textAlign: "right" }}>
              <Space size="middle">
                <Button
                  icon={<ForkOutlined />}
                  onClick={() => message.info("Opening in Builder...")}
                  size="medium"
                  style={{
                    borderRadius: "6px",
                    fontSize: "16px",
                    padding: "0 24px",
                    borderColor: "#d1d5db", // Gray-300
                    color: "#374151", // Gray-700
                    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  }}
                >
                  Fork in Builder
                </Button>
                <Button
                  icon={<CopyOutlined />}
                  onClick={handleCopyJSON}
                  size="medium"
                  style={{
                    borderRadius: "6px",
                    fontSize: "16px",
                    padding: "0 24px",
                    borderColor: "#d1d5db", // Gray-300
                    color: "#374151", // Gray-700
                    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  }}
                >
                  Copy JSON
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="medium"
                  style={{
                    backgroundColor: "#2563eb", // Blue-600
                    borderColor: "#2563eb",
                    borderRadius: "6px",
                    fontSize: "16px",
                    padding: "0 24px",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                  }}
                >
                  {loading ? "Setting up..." : "Set up source"}
                </Button>
              </Space>
            </Form.Item>
          </Form>

          {/* Need help section */}
          <div
            style={{
              textAlign: "center",
              marginTop: "32px",
              paddingTop: "24px",
              borderTop: "1px solid #e5e7eb", // Light border
            }}
          >
            <Text style={{ color: "#6b7280", fontSize: "14px", marginRight: "16px" }}>
              Need help from a teammate to set up the source?
            </Text>
            <Button
              icon={<UserAddOutlined />}
              onClick={() => message.info("Invite dialog opened")}
              style={{
                borderRadius: "6px",
                height: "38px",
                fontSize: "14px",
                padding: "0 16px",
                borderColor: "#d1d5db",
                color: "#374151",
                boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
              }}
            >
              Invite users
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
